import React, { useState, useEffect, useMemo } from 'react';
import {
	ChevronLeft,
	Search,
	ListOrdered,
	Truck,
	MapPin,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { agentsAPI, deliveriesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { getDisplayAddress, hideImported, formatValuationNo, getPropertyBillPrimary, getPropertyBillSecondary } from '../../utils/displayName';
import AgentBottomNav from './AgentBottomNav';

// Haversine distance in metres between (lat1, lng1) and (lat2, lng2)
function distanceMetres(lat1, lng1, lat2, lng2) {
	const R = 6371e3;
	const φ1 = (lat1 * Math.PI) / 180;
	const φ2 = (lat2 * Math.PI) / 180;
	const Δφ = ((lat2 - lat1) * Math.PI) / 180;
	const Δλ = ((lng2 - lng1) * Math.PI) / 180;
	const a =
		Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
		Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

function billCoords(bill) {
	const lat = bill.gpsLat ?? bill.gps_lat ?? bill.latitude;
	const lng = bill.gpsLng ?? bill.gps_lng ?? bill.longitude;
	if (lat == null || lng == null) return null;
	const la = Number(lat);
	const ln = Number(lng);
	if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
	return { lat: la, lng: ln };
}

function isOutstandingBill(bill) {
	const s = String(bill.status || '').toLowerCase();
	return !s || s === 'pending' || s === 'overdue';
}

const DeliverBills = () => {
	const { user, isTreatment2 } = useAuth();
	const agentId = user?.id ?? user?.userId;
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('valuation');
	const [searchQuery, setSearchQuery] = useState('');
	const [bills, setBills] = useState([]);
	const [deliveries, setDeliveries] = useState([]);
	const [loading, setLoading] = useState(true);
	const [agentLocation, setAgentLocation] = useState(null);
	const [locationLoading, setLocationLoading] = useState(false);

	const fetchAssignedBills = async () => {
		try {
			setLoading(true);
			const data = await agentsAPI.getAssignedBills(agentId);
			setBills(data || []);
		} catch (error) {
			toast.error('Failed to load bills');
			console.error('Error fetching bills:', error);
			setBills([]);
		} finally {
			setLoading(false);
		}
	};

	const fetchDeliveries = async () => {
		try {
			const data = await deliveriesAPI.getAll({ agentId });
			setDeliveries(data?.data || data || []);
		} catch (error) {
			console.error('Error fetching deliveries:', error);
			setDeliveries([]);
		}
	};

	// Treatment 2 only: request location for distance sort + km on bills. T1 does not see km / distance UX.
	useEffect(() => {
		if (!user || !isTreatment2) return;
		if (!navigator.geolocation) {
			toast.info('Location is unavailable in this browser. List order is not sorted by distance.');
			return;
		}
		setLocationLoading(true);
		const maxWait = window.setTimeout(() => {
			setLocationLoading(false);
		}, 12000);
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				window.clearTimeout(maxWait);
				setAgentLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
				setLocationLoading(false);
			},
			(err) => {
				window.clearTimeout(maxWait);
				setLocationLoading(false);
				if (err?.code === 1) {
					toast.info('Location permission denied. Enable location to sort bills by distance.');
				} else {
					toast.info('Could not get your location. Bills are listed without distance sort.');
				}
			},
			{ enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
		);
		return () => window.clearTimeout(maxWait);
	}, [user, isTreatment2]);

	useEffect(() => {
		if (agentId) {
			fetchAssignedBills();
			if (activeTab === 'deliveries') {
				fetchDeliveries();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [agentId, activeTab]);

	// Pending bills: distance sort (and km) only for Treatment 2 when location is available
	const pendingBills = useMemo(() => {
		const searchLower = searchQuery.toLowerCase();
		const pending = bills
			.filter(isOutstandingBill)
			.filter((bill) => {
				if (!searchLower) return true;
				const id = [
					bill.accountNumber,
					bill.newId,
					bill.valuationNo,
					bill.billNo,
				]
					.filter(Boolean)
					.join(' ')
					.toLowerCase();
				return id.includes(searchLower);
			});
		if (!isTreatment2 || !agentLocation) return pending;
		return [...pending].sort((a, b) => {
			const cA = billCoords(a);
			const cB = billCoords(b);
			const hasA = !!cA;
			const hasB = !!cB;
			if (!hasA && !hasB) return 0;
			if (!hasA) return 1;
			if (!hasB) return -1;
			const distA = distanceMetres(agentLocation.lat, agentLocation.lng, cA.lat, cA.lng);
			const distB = distanceMetres(agentLocation.lat, agentLocation.lng, cB.lat, cB.lng);
			return distA - distB;
		});
	}, [bills, agentLocation, searchQuery, isTreatment2]);

	const deliveredBills = bills.filter((bill) => String(bill.status || '').toLowerCase() === 'delivered');

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-[#001E3C] border-b border-white/10'>
				<div className='flex items-center gap-4 mb-4'>
					<button
						type='button'
						onClick={(e) => {
							e.preventDefault();
							navigate('/agent/dashboard');
						}}
						className='glass-card p-3 rounded-xl'>
						<ChevronLeft className='w-5 h-5 text-white' />
					</button>
					<h2 className='text-xl font-black uppercase text-white'>
						Deliver Bills
					</h2>
				</div>

				{/* Tabs */}
				<div className='grid grid-cols-3 gap-4'>
					<button
						type='button'
						onClick={() => setActiveTab('valuation')}
						className={`glass-card p-3 text-center transition-all duration-200 ${
							activeTab === 'valuation' 
								? 'border-2 border-[#FFC107] ring-2 ring-[#FFC107]/30' 
								: 'border border-transparent hover:border-white/20'
						}`}>
						<ListOrdered className={`w-5 h-5 mx-auto mb-1 ${activeTab === 'valuation' ? 'text-[#FFC107]' : 'text-white'}`} />
						<span className={`text-xs font-bold ${activeTab === 'valuation' ? 'text-[#FFC107]' : 'text-white'}`}>Pending</span>
					</button>
					<button
						type='button'
						onClick={() => setActiveTab('deliveries')}
						className={`glass-card p-3 text-center transition-all duration-200 ${
							activeTab === 'deliveries' 
								? 'border-2 border-[#00E676] ring-2 ring-[#00E676]/30' 
								: 'border border-transparent hover:border-white/20'
						}`}>
						<Truck className={`w-5 h-5 mx-auto mb-1 ${activeTab === 'deliveries' ? 'text-[#00E676]' : 'text-white'}`} />
						<span className={`text-xs font-bold ${activeTab === 'deliveries' ? 'text-[#00E676]' : 'text-white'}`}>Deliveries</span>
					</button>
				</div>
			</header>

			<main className='flex-1 overflow-y-auto p-6 pb-20 no-scrollbar'>
				{activeTab === 'valuation' && (
					<>
						<div className='mb-6'>
							<div className='relative'>
								<Search className='absolute left-4 top-3.5 w-5 h-5 text-white/30' />
								<input
									type='text'
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className='w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white placeholder-white/30'
									placeholder='Search valuation numbers...'
								/>
							</div>
							<div className='mt-3 flex items-center gap-2 flex-wrap'>
								{isTreatment2 && locationLoading && (
									<span className='text-xs text-white/50'>Getting location…</span>
								)}
								{isTreatment2 && !locationLoading && !agentLocation && (
									<span className='text-xs text-white/40'>Distance sort needs location access</span>
								)}
								{isTreatment2 && pendingBills.length > 0 && agentLocation && billCoords(pendingBills[0]) && (
								<button
										type='button'
										onClick={(e) => {
											e.stopPropagation();
											const c = billCoords(pendingBills[0]);
											if (!c) return;
											window.open(`https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`, '_blank');
										}}
										className='flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40'>
										<MapPin className='w-4 h-4' />
										Navigate to next
									</button>
								)}
							</div>
						</div>

						{loading ? (
							<div className='text-center py-12'>
								<p className='text-white/60'>Loading...</p>
							</div>
						) : (
							<div className='space-y-4'>
								{pendingBills.map((bill) => {
									const c = billCoords(bill);
									const distKm =
										isTreatment2 && agentLocation && c
											? (distanceMetres(agentLocation.lat, agentLocation.lng, c.lat, c.lng) / 1000).toFixed(1)
											: null;
									return (
										<div
											key={getPropertyBillPrimary(bill)}
											onClick={() => navigate(`/agent/property/${encodeURIComponent(bill.id || formatValuationNo(bill.valuationNo) || bill.billNo)}`)}
											className='glass-card p-4 border-l-4 border-[#FFC107] active:scale-95 transition-transform cursor-pointer'>
											<div className='flex justify-between items-start mb-2 text-white'>
												<div>
													{/* <p className='font-mono font-bold text-sm'>
														{getPropertyBillPrimary(bill)}
													</p> */}
													{getPropertyBillSecondary(bill) && (
														<p className='text-xs text-white font-bold'>
															Valuation: {getPropertyBillSecondary(bill)}
														</p>
													)}
												</div>
												<div className='flex items-center gap-2'>
													{isTreatment2 && distKm != null && (
														<span className='text-xs font-bold text-white/60'>{distKm} km</span>
													)}
													<span className='status-badge status-pending'>Pending</span>
												</div>
											</div>
											<div className='flex justify-between items-center'>
												{bill.dueDate && (
													<p className='text-xs text-white/60'>Due: {new Date(bill.dueDate).toLocaleDateString()}</p>
												)}
												<p className='text-sm font-bold text-[#FFC107]'>
													₵{(Number(bill.totalDue ?? bill.total_due ?? bill.currentAmount ?? bill.current_amount) || 0).toLocaleString()}
												</p>
											</div>
										</div>
									);
								})}
								{pendingBills.length === 0 && !loading && (
									<p className='text-center text-white/50 text-sm'>No pending bills</p>
								)}
							</div>
						)}
					</>
				)}

				{activeTab === 'deliveries' && (
					<div className='space-y-4'>
						{/* Show deliveries from API first (most accurate) */}
						{deliveries.length > 0 && (
							<>
								{deliveries.map((delivery) => {
									const deliveryDate = delivery.deliveryDate 
										? new Date(delivery.deliveryDate).toISOString().split('T')[0]
										: '';
									return (
										<div
											key={delivery.id}
											className='glass-card p-4 border-l-4 border-[#00E676]'>
											<div className='flex justify-between items-start mb-2 text-white'>
												<div>
													<p className='font-mono font-bold text-sm'>
														{getPropertyBillPrimary(delivery.property) || 'N/A'}
													</p>
													{delivery.property?.streetName && (
														<p className='text-sm font-bold'>
															{getDisplayAddress(delivery.property)}
														</p>
													)}
												</div>
												<span className='status-badge status-delivered'>Delivered</span>
											</div>
											<div className='flex justify-between items-center mt-2'>
												<div className='text-xs text-white/60 space-y-1'>
													<p>To: {hideImported(delivery.recipientName)}</p>
													{deliveryDate && <p>Date: {deliveryDate}</p>}
												</div>
												<p className='text-sm font-bold text-[#00E676]'>
													₵{(Number(delivery.property?.totalDue ?? delivery.property?.total_due ?? delivery.property?.currentAmount ?? delivery.property?.current_amount) || 0).toLocaleString()}
												</p>
											</div>
										</div>
									);
								})}
							</>
						)}
						
						{/* Fallback to deliveredBills if no deliveries from API */}
						{deliveries.length === 0 && deliveredBills.map((bill) => {
							const deliveryDate = bill.deliveryDate 
								? new Date(bill.deliveryDate).toISOString().split('T')[0]
								: '';
							return (
								<div
									key={getPropertyBillPrimary(bill)}
									className='glass-card p-4 border-l-4 border-[#00E676]'>
									<div className='flex justify-between items-start mb-2 text-white'>
										<div>
											<p className='font-mono font-bold text-sm'>
												{getPropertyBillPrimary(bill)}
											</p>
											{bill.streetName && (
												<p className='text-sm font-bold'>
													{getDisplayAddress(bill)}
												</p>
											)}
										</div>
										<span className='status-badge status-delivered'>Delivered</span>
									</div>
									<div className='flex justify-between items-center mt-2'>
										<div className='text-xs text-white/60 space-y-1'>
											{bill.recipientName && (
												<p>To: {hideImported(bill.recipientName)}</p>
											)}
											{deliveryDate && <p>Date: {deliveryDate}</p>}
										</div>
										<p className='text-sm font-bold text-[#00E676]'>
											₵{(Number(bill.totalDue ?? bill.total_due ?? bill.currentAmount ?? bill.current_amount) || 0).toLocaleString()}
										</p>
									</div>
								</div>
							);
						})}
						
						{deliveredBills.length === 0 && deliveries.length === 0 && (
							<p className='text-center text-white/50 text-sm'>No deliveries recorded</p>
						)}
					</div>
				)}
			</main>

			{/* Static Footer Navigation */}
			<AgentBottomNav active='bills' />
		</div>
	);
};

export default DeliverBills;




